import { type FC, Suspense, useRef } from 'react';
import classnames from 'classnames';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';

import dibsCss from 'dibs-css';
import { VideoTile } from 'dibs-elements/exports/VideoTile.ts';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';

import { useHomePageContext } from '../HomePageContext.tsx';

import styles from './HpSharedVideoComponentVideo.scss';

import { type HpSharedVideoComponentVideo_componentModule$key } from './__generated__/HpSharedVideoComponentVideo_componentModule.graphql.ts';
import { type HpSharedVideoComponentVideoQuery as HpSharedVideoComponentVideoQueryType } from './__generated__/HpSharedVideoComponentVideoQuery.graphql.ts';

type Props = {
    componentModule: HpSharedVideoComponentVideo_componentModule$key;
};

const componentModuleFragment = graphql`
    fragment HpSharedVideoComponentVideo_componentModule on VideoModule {
        videoId
        videoAutoplay
    }
`;

const videoQuery = graphql`
    query HpSharedVideoComponentVideoQuery($videoId: String!) {
        viewer {
            newVideoPlayerFF: featureFlag(feature: "newVideoPlayer")
            video(videoId: $videoId) {
                height
                width
                desktopFile: fileByVideoSize(size: w960h540) {
                    link
                }
                mobileFile: fileByVideoSize(size: w426h240) {
                    link
                }
            }
        }
    }
`;

type VideoContentProps = {
    videoId: string;
    autoPlay: boolean;
};

const VideoContent: FC<VideoContentProps> = ({ videoId, autoPlay }) => {
    const { isMobile } = useHomePageContext();
    const playerContainerRef = useRef<HTMLVideoElement>(null);

    const data = useLazyLoadQuery<HpSharedVideoComponentVideoQueryType>(videoQuery, {
        videoId,
    });

    const newVideoPlayerFF = !!data?.viewer?.newVideoPlayerFF;
    const videoUrl = isMobile
        ? data?.viewer?.video?.mobileFile?.link
        : data?.viewer?.video?.desktopFile?.link;

    if (!videoUrl) {
        return null;
    }

    return (
        <div
            className={classnames(dibsCss.relative, styles.videoContainer, {
                [dibsCss.flex]: newVideoPlayerFF && isMobile,
                [dibsCss.justifyCenter]: newVideoPlayerFF && isMobile,
                [styles.minWFull]: newVideoPlayerFF,
            })}
        >
            {newVideoPlayerFF ? (
                <div className={classnames(dibsCss.absolute, dibsCss.inset0)}>
                    <VideoPlayer forceMute={false} ref={playerContainerRef}>
                        <VideoPlayer.Media
                            src={`https://player.vimeo.com/video/${videoId}`}
                            playback={{ autoPlay, loop: false, muted: true }}
                            objectFit="cover"
                            width={data?.viewer?.video?.width}
                            height={data?.viewer?.video?.height}
                        />
                        <VideoPlayer.Controls />
                    </VideoPlayer>
                </div>
            ) : (
                <VideoTile
                    controls
                    sourceUrl={videoUrl}
                    noSpinner
                    loop={false}
                    autoPlay={autoPlay}
                />
            )}
        </div>
    );
};

const HpSharedVideoComponentVideo: FC<Props> = ({ componentModule: componentModuleRef }) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const { videoId, videoAutoplay } = componentModule || {};

    if (!videoId) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <VideoContent videoId={videoId} autoPlay={!!videoAutoplay} />
        </Suspense>
    );
};

export default HpSharedVideoComponentVideo;
